import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Quote, Star } from "lucide-react";
import { LandingShell } from "@/components/landing/LandingShell";
import { Button } from "@/components/ui/button";
import { useLandingItems, useLandingSettings } from "@/hooks/useLandingPage";
import type { LandingItem, LandingSettings } from "@/hooks/useLandingPage";

function CtaButton({
  text,
  link,
  variant,
}: {
  text: string;
  link?: string | null;
  variant?: "default" | "outline";
}) {
  const href = link || "#contact";
  const isExternal = /^(https?:|mailto:|tel:|#)/.test(href);
  return (
    <Button asChild size="lg" variant={variant} className="text-base px-8">
      {isExternal ? (
        <a href={href}>
          {text}
          {variant !== "outline" && <ArrowRight className="ml-2 h-4 w-4" />}
        </a>
      ) : (
        <Link to={href}>
          {text}
          {variant !== "outline" && <ArrowRight className="ml-2 h-4 w-4" />}
        </Link>
      )}
    </Button>
  );
}

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

function SectionHeading({ title, subtitle }: { title?: string | null; subtitle?: string | null }) {
  if (!title && !subtitle) return null;
  return (
    <motion.div {...fadeUp} className="max-w-2xl mx-auto text-center mb-14">
      {title && (
        <h2 className="text-3xl md:text-4xl font-serif font-bold">{title}</h2>
      )}
      {subtitle && <p className="text-muted-foreground mt-4 text-lg">{subtitle}</p>}
    </motion.div>
  );
}

function Hero({ settings }: { settings: LandingSettings }) {
  return (
    <section className="relative min-h-[85vh] flex items-center overflow-hidden">
      <div className="absolute inset-0 z-0">
        {settings.hero_video_url ? (
          <video
            className="w-full h-full object-cover"
            src={settings.hero_video_url}
            poster={settings.hero_poster_url ?? undefined}
            autoPlay
            muted
            loop
            playsInline
          />
        ) : settings.hero_image_url ? (
          <img
            src={settings.hero_image_url}
            alt={settings.hero_title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-background via-card to-background" />
        )}
        <div
          className="absolute inset-0 bg-charcoal"
          style={{ opacity: Math.min(settings.hero_overlay_opacity ?? 35, 55) / 100 }}
        />
      </div>

      <div className="container mx-auto px-6 relative z-10 py-24">
        <motion.div {...fadeUp} className="max-w-3xl text-cream [text-shadow:0_1px_8px_hsl(var(--charcoal))]">
          {settings.hero_eyebrow && (
            <span className="text-primary text-sm font-medium tracking-[0.3em] uppercase">
              {settings.hero_eyebrow}
            </span>
          )}
          <h1 className="text-4xl md:text-6xl font-serif font-bold mt-4 mb-6 leading-tight">
            {settings.hero_title}
          </h1>
          {settings.hero_subtitle && (
            <p className="text-lg md:text-xl text-cream/90 mb-10 max-w-2xl">
              {settings.hero_subtitle}
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-4">
            {settings.primary_button_text && (
              <CtaButton text={settings.primary_button_text} link={settings.primary_button_link} />
            )}
            {settings.secondary_button_text && (
              <CtaButton
                text={settings.secondary_button_text}
                link={settings.secondary_button_link}
                variant="outline"
              />
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Features({ settings, items }: { settings: LandingSettings; items: LandingItem[] }) {
  if (!settings.features_enabled || items.length === 0) return null;
  return (
    <section id="features" className="py-24">
      <div className="container mx-auto px-6">
        <SectionHeading title={settings.features_title} subtitle={settings.features_subtitle} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {items.map((item, i) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="p-8 rounded-xl border border-border bg-card/50"
            >
              {item.image_url ? (
                <img
                  src={item.image_url}
                  alt={item.title}
                  loading="lazy"
                  className="w-full h-40 object-cover rounded-lg mb-5"
                />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-5">
                  <Check className="h-5 w-5" />
                </div>
              )}
              <h3 className="text-xl font-serif font-semibold mb-3">{item.title}</h3>
              {item.body && <p className="text-muted-foreground">{item.body}</p>}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Gallery({ settings, items }: { settings: LandingSettings; items: LandingItem[] }) {
  if (!settings.gallery_enabled || items.length === 0) return null;
  return (
    <section id="gallery" className="py-24 bg-card/30">
      <div className="container mx-auto px-6">
        <SectionHeading title={settings.gallery_title} subtitle={settings.gallery_subtitle} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item) => (
            <motion.div
              key={item.id}
              {...fadeUp}
              className="group relative aspect-[4/3] overflow-hidden rounded-xl border border-border"
            >
              {item.video_url ? (
                <video
                  src={item.video_url}
                  poster={item.image_url ?? undefined}
                  muted
                  loop
                  playsInline
                  autoPlay
                  className="w-full h-full object-cover"
                />
              ) : (
                <img
                  src={item.image_url ?? ""}
                  alt={item.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              )}
              <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-charcoal/70 to-transparent">
                <p className="font-medium text-cream">{item.title}</p>
                {item.subtitle && (
                  <p className="text-sm text-cream/85">{item.subtitle}</p>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials({ settings, items }: { settings: LandingSettings; items: LandingItem[] }) {
  if (!settings.testimonials_enabled || items.length === 0) return null;
  return (
    <section className="py-24">
      <div className="container mx-auto px-6">
        <SectionHeading
          title={settings.testimonials_title}
          subtitle={settings.testimonials_subtitle}
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {items.map((item, i) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="p-8 rounded-xl border border-border bg-card/50"
            >
              <Quote className="h-6 w-6 text-primary mb-4" />
              {item.body && <p className="text-muted-foreground italic mb-6">{item.body}</p>}
              <div className="flex items-center gap-3">
                {item.image_url && (
                  <img
                    src={item.image_url}
                    alt={item.title}
                    loading="lazy"
                    className="w-10 h-10 rounded-full object-cover"
                  />
                )}
                <div>
                  <p className="font-medium">{item.title}</p>
                  {item.subtitle && (
                    <p className="text-sm text-muted-foreground">{item.subtitle}</p>
                  )}
                </div>
              </div>
              {item.rating ? (
                <div className="flex gap-1 mt-4">
                  {Array.from({ length: item.rating }).map((_, idx) => (
                    <Star key={idx} className="h-4 w-4 fill-primary text-primary" />
                  ))}
                </div>
              ) : null}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}


function FinalCta({ settings }: { settings: LandingSettings }) {
  if (!settings.cta_enabled || !settings.cta_title) return null;
  return (
    <section id="contact" className="py-24">
      <div className="container mx-auto px-6">
        <motion.div
          {...fadeUp}
          className="max-w-3xl mx-auto text-center rounded-2xl border border-border bg-card/50 p-12"
        >
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4">{settings.cta_title}</h2>
          {settings.cta_subtitle && (
            <p className="text-lg text-muted-foreground mb-8">{settings.cta_subtitle}</p>
          )}
          <CtaButton
            text={settings.cta_button_text || "Get in touch"}
            link={settings.cta_button_link}
          />
        </motion.div>
      </div>
    </section>
  );
}

export function LandingPageView() {
  const { data: settings, isLoading } = useLandingSettings();
  const { data: items = [] } = useLandingItems(true);

  if (isLoading || !settings) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const bySection = (section: string) => items.filter((i) => i.section === section);

  return (
    <LandingShell settings={settings}>
      <Hero settings={settings} />
      <Features settings={settings} items={bySection("feature")} />
      <Gallery settings={settings} items={bySection("gallery")} />
      <Testimonials settings={settings} items={bySection("testimonial")} />
      <FinalCta settings={settings} />
    </LandingShell>
  );
}

export default LandingPageView;
